import {useState} from 'react';
import {createMutationErrorHandler, trpc} from '@common-web/lib/trpc';
import type {ViewableFile} from '@internal/pages/group-pipeline/detail/ftp/FileViewer';

// Blob URLs are dropped once the tab or download has had time to read them.
const RELEASE_AFTER_MS = 60_000;

interface FtpFile {
  fileName: string;
  contentType: string;
  base64: string;
}

// A blob URL for a file the API sent as base64.
export function fileUrl(file: {base64: string; contentType: string}) {
  const bytes = Uint8Array.from(atob(file.base64), (c) => c.charCodeAt(0));
  return URL.createObjectURL(new Blob([bytes], {type: file.contentType}));
}

const save = (url: string, fileName: string) => {
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
};

type FileAction = 'view' | 'open' | 'download';

const FAILED: Record<FileAction, string> = {
  view: "Couldn't show the file",
  open: "Couldn't open the file",
  download: "Couldn't download the file",
};

// What the access log records for each action.
const LOGGED_AS = {view: 'VIEWED', open: 'OPENED', download: 'DOWNLOADED'} as const;
type LoggedAction = (typeof LOGGED_AS)[FileAction];

// Shows, opens or saves an FTP file fetched through the API; `key` says which file to fetch.
// A viewed file is held in `viewing` for FileViewerDialog until `closeViewer`.
function useFtpFile(
  vbaGroupId: string,
  fetchFile: (key: string, action: LoggedAction) => Promise<FtpFile>,
) {
  const utils = trpc.useUtils();
  const [loading, setLoading] = useState<string | null>(null);
  const [viewing, setViewing] = useState<ViewableFile | null>(null);

  const run = async (
    action: FileAction,
    key: string,
    use: (url: string, file: FtpFile) => void,
    onError?: () => void,
  ) => {
    setLoading(`${action}:${key}`);
    try {
      const file = await fetchFile(key, LOGGED_AS[action]);
      void utils.internal.groupPipeline.activity.invalidate({vbaGroupId});
      const url = fileUrl(file);
      if (action !== 'view') setTimeout(() => URL.revokeObjectURL(url), RELEASE_AFTER_MS);
      use(url, file);
    } catch (error) {
      onError?.();
      createMutationErrorHandler(FAILED[action])(error);
    } finally {
      setLoading(null);
    }
  };

  return {
    isLoading: (action: FileAction, key: string) => loading === `${action}:${key}`,
    viewing,
    closeViewer: () => {
      if (viewing) URL.revokeObjectURL(viewing.url);
      setViewing(null);
    },
    view: (key: string) =>
      run('view', key, (url, file) =>
        setViewing({url, name: file.fileName, contentType: file.contentType}),
      ),
    // The tab opens on the click so it isn't blocked as a popup, then shows the file once loaded.
    // If the browser blocks it anyway, the file is downloaded instead.
    open: (key: string) => {
      const tab = window.open('', '_blank');
      void run(
        'open',
        key,
        (url, file) => {
          if (tab) tab.location.href = url;
          else save(url, file.fileName);
        },
        () => tab?.close(),
      );
    },
    download: (key: string) => run('download', key, (url, file) => save(url, file.fileName)),
  };
}

// A document's linked file, by document id.
export function useDocumentFile(vbaGroupId: string) {
  const utils = trpc.useUtils();
  return useFtpFile(vbaGroupId, (documentId, action) =>
    utils.internal.groupFtp.documentFile.fetch({vbaGroupId, documentId, action}, {gcTime: 0}),
  );
}

// Any file in the group's FTP folder, by path.
export function useGroupFile(vbaGroupId: string) {
  const utils = trpc.useUtils();
  return useFtpFile(vbaGroupId, (path, action) =>
    utils.internal.groupFtp.groupFile.fetch({vbaGroupId, path, action}, {gcTime: 0}),
  );
}
